import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { advanceService } from '../services/api';
import { DollarSign, CheckCircle, XCircle, Clock, AlertCircle, Sparkles, Calendar, ShieldCheck, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const AdvanceRequest = () => {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('');
  const [isActive, setIsActive] = useState(false);

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Buscar solicitação atual do colaborador
  const { data: requestData, isLoading } = useQuery({
    queryKey: ['my-advance-request'],
    queryFn: () => advanceService.getMyRequest(),
    staleTime: 30000,
  });

  const currentRequest = requestData?.data?.data;
  const salaryInfo = requestData?.data?.salary_info || { base_salary: 0, max_limit: 550, percentage: 40 };
  const windowInfo = requestData?.data?.window_info;
  const maxLimit = Number(salaryInfo.max_limit || 550);
  const baseSalary = Number(salaryInfo.base_salary || 0);

  // Verificar se está no período permitido (calculado pelo backend em fuso BRT com prorrogação de final de semana)
  const isRequestPeriod = React.useMemo(() => {
    if (windowInfo && typeof windowInfo.is_open === 'boolean') {
      return windowInfo.is_open;
    }
    // Fallback calculando localmente no fuso oficial de Brasília
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    });
    const parts = formatter.formatToParts(now);
    const partMap = {};
    parts.forEach(p => { partMap[p.type] = p.value; });
    const currentYear = parseInt(partMap.year, 10);
    const currentMonth = parseInt(partMap.month, 10);
    const currentDay = parseInt(partMap.day, 10);

    let bizCount = 0;
    let endDay = 14;
    for (let d = 13; d <= 20; d++) {
      const dt = new Date(currentYear, currentMonth - 1, d);
      const isBiz = dt.getDay() !== 0 && dt.getDay() !== 6;
      if (isBiz) bizCount++;
      if (bizCount >= 2 && d >= 14) {
        endDay = d;
        break;
      }
    }
    return currentDay >= 13 && currentDay <= endDay;
  }, [windowInfo]);

  // Verificar se pode solicitar (período correto e não aprovado ainda)
  const canRequest = React.useMemo(() => {
    if (!isRequestPeriod) return false;
    if (currentRequest?.status === 'APPROVED') return false;
    return true;
  }, [isRequestPeriod, currentRequest]);

  // Atualizar estado quando receber dados
  React.useEffect(() => {
    if (currentRequest) {
      setIsActive(currentRequest.status === 'APPROVED' || currentRequest.status === 'PENDING');
      if (currentRequest.amount) {
        setAmount(String(currentRequest.amount));
      }
    } else {
      setIsActive(false);
      setAmount('');
    }
  }, [currentRequest]);

  // Mutação para solicitar/atualizar adiantamento
  const requestMutation = useMutation({
    mutationFn: (data) => advanceService.requestAdvance(data),
    onSuccess: (response, variables) => {
      toast.success(response.data?.message || 'Solicitação enviada para aprovação!');
      queryClient.invalidateQueries({ queryKey: ['my-advance-request'] });
      if (!variables.isActive) {
        setAmount('');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao processar solicitação');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const numericAmount = Number(amount);
    if (isActive && (!amount || numericAmount < 1 || numericAmount > maxLimit)) {
      toast.error(`Valor deve estar entre R$ 1,00 e ${maxLimit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`);
      return;
    }

    requestMutation.mutate({
      amount: isActive ? numericAmount : 0,
      isActive,
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Em Análise pelo Responsável', icon: Clock },
      APPROVED: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Aprovado', icon: CheckCircle },
      REJECTED: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Rejeitado', icon: XCircle },
    };
    return badges[status] || badges.PENDING;
  };

  const formattedMaxLimit = maxLimit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formattedBaseSalary = baseSalary > 0
    ? baseSalary.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;

  const windowEndDay = windowInfo?.end_day || 14;
  const isExtendedWindow = windowInfo?.is_extended || windowEndDay > 14;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-blue-600" />
            Adiantamento Mensal (Quinzena)
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Solicite até <strong className="text-gray-900">{formattedMaxLimit}</strong> para este mês (até 40% do seu salário base)
          </p>
        </div>
        {formattedBaseSalary && (
          <div className="bg-blue-50/80 border border-blue-200 rounded-xl px-3 py-1.5 text-xs text-blue-900 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span>Salário Base: <strong>{formattedBaseSalary}</strong> (Teto 40%: <strong>{formattedMaxLimit}</strong>)</span>
          </div>
        )}
      </div>

      {/* BANNER CHAMATIVO: SOLICITAÇÃO ENVIADA E EM ANÁLISE */}
      {currentRequest?.status === 'PENDING' && (
        <div className="bg-gradient-to-r from-amber-500/15 via-yellow-500/20 to-orange-500/15 border-2 border-amber-400 rounded-2xl p-5 sm:p-6 shadow-md relative overflow-hidden animate-fadeIn">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3.5 bg-amber-500 text-white rounded-2xl shadow-md flex-shrink-0 animate-pulse">
                <Clock className="w-7 h-7 sm:w-8 sm:h-8" />
              </div>
              <div className="space-y-1.5">
                <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-black uppercase tracking-wider bg-amber-200 text-amber-950 border border-amber-300">
                  <Sparkles className="w-3.5 h-3.5 text-amber-700" />
                  Solicitação Registrada com Sucesso!
                </div>
                <h3 className="text-lg sm:text-xl font-black text-gray-900">
                  Sua solicitação de adiantamento está em análise pelo responsável
                </h3>
                <p className="text-sm text-gray-800 leading-relaxed max-w-2xl">
                  Registramos o pedido no valor de{' '}
                  <strong className="font-mono text-amber-950 font-black text-base px-2 py-0.5 bg-amber-200/70 rounded-md">
                    {Number(currentRequest.amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </strong>
                  . A gerência foi notificada e está avaliando. Assim que for homologado, o valor será programado para liberação e descontado no fechamento da sua folha.
                </p>
                <div className="pt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-amber-950 font-medium">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-amber-700" />
                    Mês de Referência: <strong>{monthNames[currentRequest.ref_month - 1]}/{currentRequest.ref_year}</strong>
                  </span>
                  <span>
                    • Enviado em: <strong>{new Date(currentRequest.request_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta de período de solicitação */}
      {isRequestPeriod ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-900">
              <p className="font-semibold mb-0.5">Período de Solicitação Aberto!</p>
              <p>
                Você pode solicitar seu adiantamento quinzenal agora.{' '}
                {isExtendedWindow
                  ? `Prazo prorrogado até dia ${windowEndDay} devido ao final de semana.`
                  : `O prazo regular termina no dia ${windowEndDay}.`}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-0.5">Período de Solicitação Fechado</p>
              <p>
                As solicitações de adiantamento mensal abrem a partir do dia <strong>13</strong> de cada mês (garantindo no mínimo 2 dias úteis, com prorrogação em fins de semana).
                {(() => {
                  const today = new Date();
                  const dayOfMonth = today.getDate();
                  if (dayOfMonth < 13) {
                    const daysUntil = 13 - dayOfMonth;
                    return ` O próximo período abrirá em ${daysUntil} dia(s).`;
                  }
                  return ' O período para este mês foi encerrado. Uma nova janela abrirá no dia 13 do próximo mês.';
                })()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Card de informações e regras */}
      <div className="bg-blue-50/70 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900 space-y-1">
            <p className="font-semibold">Como funciona o Adiantamento Quinzenal:</p>
            <ul className="list-disc list-inside space-y-0.5 text-blue-800">
              <li>
                Você pode solicitar qualquer valor entre R$ 1,00 e <strong>{formattedMaxLimit}</strong> (até 40% do seu salário base).
              </li>
              <li>
                A janela abre no dia <strong>13</strong> de cada mês (prorrogada até o próximo dia útil se cair em sábado ou domingo).
              </li>
              <li>A solicitação é avaliada e homologada pela gerência responsável.</li>
              <li>Após a aprovação, o valor recebido é descontado automaticamente na sua folha mensal.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Status da Solicitação Aprovada ou Rejeitada */}
      {currentRequest && currentRequest.status !== 'PENDING' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h2 className="text-lg font-bold text-gray-900">Status da Sua Solicitação</h2>
            {(() => {
              const badge = getStatusBadge(currentRequest.status);
              const Icon = badge.icon;
              return (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${badge.bg} ${badge.text}`}>
                  <Icon className="w-4 h-4" />
                  {badge.label}
                </span>
              );
            })()}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-xl">
              <span className="text-xs text-gray-500 block">Valor Solicitado</span>
              <span className="text-xl font-bold text-gray-900">
                {Number(currentRequest.amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl">
              <span className="text-xs text-gray-500 block">Competência</span>
              <span className="text-base font-semibold text-gray-800">
                {monthNames[currentRequest.ref_month - 1]}/{currentRequest.ref_year}
              </span>
            </div>
          </div>

          {currentRequest.status === 'APPROVED' && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2">
              <p className="text-sm font-bold text-emerald-900 flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" />
                Adiantamento Homologado e Aprovado
              </p>
              <p className="text-xs text-emerald-800">
                O valor de {Number(currentRequest.amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} foi autorizado
                {currentRequest.approver_name && ` por ${currentRequest.approver_name}`}
                {currentRequest.approved_at && ` em ${new Date(currentRequest.approved_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                . O lançamento será abatido no holerite deste mês.
              </p>
            </div>
          )}

          {currentRequest.rejection_reason && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl space-y-1">
              <p className="text-sm font-bold text-rose-900 flex items-center gap-2">
                <XCircle className="w-4 h-4 text-rose-600" />
                Motivo da Rejeição:
              </p>
              <p className="text-xs text-rose-800">{currentRequest.rejection_reason}</p>
            </div>
          )}
        </div>
      )}

      {/* Formulário de Solicitação / Atualização */}
      {currentRequest?.status !== 'APPROVED' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {currentRequest?.status === 'PENDING' ? 'Alterar Solicitação Pendente' : 'Solicitar Adiantamento'}
          </h2>

          {!canRequest && (
            <div className="mb-4 p-4 bg-rose-50 border border-rose-200 rounded-xl">
              <p className="text-sm text-rose-900">
                {!isRequestPeriod 
                  ? `As solicitações só podem ser enviadas durante o período de quinzena (de 13 a ${windowEndDay}).`
                  : 'Você já possui uma solicitação aprovada para este mês.'}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => {
                  setIsActive(e.target.checked);
                  if (!e.target.checked) {
                    setAmount('');
                  }
                }}
                disabled={!canRequest}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
              />
              <label htmlFor="isActive" className={`text-sm font-semibold ${!canRequest ? 'text-gray-400' : 'text-gray-800'}`}>
                Desejo solicitar adiantamento mensal para este mês
              </label>
            </div>

            {isActive && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                    Valor do adiantamento (R$)
                  </label>
                  <button
                    type="button"
                    onClick={() => setAmount(String(maxLimit))}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline"
                  >
                    Usar valor máximo ({formattedMaxLimit})
                  </button>
                </div>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    id="amount"
                    min="1"
                    max={maxLimit}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Digite um valor de 1 a ${maxLimit}`}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                    required={isActive}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Valor mínimo: R$ 1,00 | Limite máximo: <strong>{formattedMaxLimit}</strong> (40% do salário base cadastrado)
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={requestMutation.isLoading || (isActive && !amount) || !canRequest}
                className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-all"
              >
                {requestMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enviando solicitação...
                  </>
                ) : (
                  currentRequest?.status === 'PENDING' ? 'Atualizar Solicitação' : 'Enviar Solicitação'
                )}
              </button>
              {isActive && amount && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl">
                  <span className="text-xs text-gray-600">Total a receber:</span>
                  <span className="text-base font-bold text-gray-900">
                    {Number(amount || 0).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </span>
                </div>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdvanceRequest;
